import { BadgeStatus, Priority } from "../../enums/Status";
import {
  ColumnType,
  TableCellType,
  TaskDataSource,
} from "../../models/tableModels";
import {
  SubjectType,
  TaskColumnType,
  TaskPriority,
  TaskStatus,
} from "../../models/workflowModels";
import { NULLABLE_VALUES } from "../../constants";
import { TitleWithCircleTagProps } from "../../components/tables/TitleWithCircleTag";
import { AvatarProps } from "../../components/visuals/Avatar";
import React from "react";
import Input from "antd/es/input";
import Button from "antd/es/button";
import Space from "antd/es/space";
import { SearchOutlined } from "@ant-design/icons";
import type { FilterDropdownProps } from "antd/es/table/interface";

const PRIORITY_WEIGHT: Record<string, number> = {
  [TaskPriority.HIGH]: 3,
  [TaskPriority.MEDIUM]: 2,
  [TaskPriority.LOW]: 1,
};

const STATUS_WEIGHT: Record<string, number> = {
  [TaskStatus.STUCK]: 4,
  [TaskStatus.IN_PROGRESS]: 3,
  [TaskStatus.NOT_STARTED]: 2,
  [TaskStatus.COMPLETED]: 1,
};

const str = (v: unknown): string => (typeof v === "string" ? v : "");

export const TASK_COLUMNS: TaskColumnType[] = [
  {
    title: "Title",
    dataIndex: "title",
    key: "title",
    fixed: "left",
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }: FilterDropdownProps) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder="Search tasks"
          value={selectedKeys[0]}
          onChange={(e): void =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={(): void => confirm()}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={(): void => confirm()}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={(): void => {
              clearFilters && clearFilters();
              confirm();
            }}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value: boolean | React.Key, record: TaskDataSource): boolean => {
      const title = str(
        (record.title as TitleWithCircleTagProps)?.title,
      ).toLowerCase();
      const desc = str(record.description as string).toLowerCase();
      const search = String(value).toLowerCase();
      return title.includes(search) || desc.includes(search);
    },
    sorter: (a: TaskDataSource, b: TaskDataSource): number => {
      const tA = str((a.title as TitleWithCircleTagProps)?.title).toLowerCase();
      const tB = str((b.title as TitleWithCircleTagProps)?.title).toLowerCase();
      return tA.localeCompare(tB);
    },
  },
  {
    title: "Assigned To",
    dataIndex: "assigned_to",
    key: "assigned_to",
    onFilter: (value: boolean | React.Key, record: TaskDataSource) =>
      str((record.assigned_to as AvatarProps)?.username) === String(value),
    filterMultiple: true,
    sorter: (a: TaskDataSource, b: TaskDataSource): number => {
      const uA = str((a.assigned_to as AvatarProps)?.username).toLowerCase();
      const uB = str((b.assigned_to as AvatarProps)?.username).toLowerCase();
      return uA.localeCompare(uB);
    },
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    filters: [
      { text: "Not Started", value: TaskStatus.NOT_STARTED },
      { text: "In Progress", value: TaskStatus.IN_PROGRESS },
      { text: "Completed", value: TaskStatus.COMPLETED },
      { text: "Stuck", value: TaskStatus.STUCK },
    ],
    onFilter: (value: boolean | React.Key, record: TaskDataSource) =>
      str(record._rawStatus) === String(value),
    filterMultiple: true,
    sorter: (a: TaskDataSource, b: TaskDataSource) =>
      (STATUS_WEIGHT[str(a._rawStatus)] || 0) -
      (STATUS_WEIGHT[str(b._rawStatus)] || 0),
  },
  {
    title: "Date Due",
    dataIndex: "date_due",
    key: "date_due",
    sorter: (a: TaskDataSource, b: TaskDataSource): number => {
      const dA = str(a._rawDateDue);
      const dB = str(b._rawDateDue);
      const isNullA = !dA || NULLABLE_VALUES.includes(dA);
      const isNullB = !dB || NULLABLE_VALUES.includes(dB);
      if (isNullA && isNullB) return 0;
      if (isNullA) return 1;
      if (isNullB) return -1;
      return new Date(dA).getTime() - new Date(dB).getTime();
    },
  },
  { title: "Notes", dataIndex: "note_count", key: "note_count" },
  {
    title: "Priority",
    dataIndex: "priority",
    key: "priority",
    filters: [
      { text: "High", value: TaskPriority.HIGH },
      { text: "Medium", value: TaskPriority.MEDIUM },
      { text: "Low", value: TaskPriority.LOW },
    ],
    onFilter: (value: boolean | React.Key, record: TaskDataSource) =>
      str(record.priority) === String(value),
    filterMultiple: true,
    sorter: (a: TaskDataSource, b: TaskDataSource) =>
      (PRIORITY_WEIGHT[str(a.priority)] || 0) -
      (PRIORITY_WEIGHT[str(b.priority)] || 0),
  },
  {
    title: "Last Updated",
    dataIndex: "last_updated",
    key: "last_updated",
    sorter: (a: TaskDataSource, b: TaskDataSource): number => {
      const mA = str(a._rawLastModified);
      const mB = str(b._rawLastModified);
      if (!mA && !mB) return 0;
      if (!mA) return 1;
      if (!mB) return -1;
      return new Date(mA).getTime() - new Date(mB).getTime();
    },
  },
];

export const TASK_COLUMN_TYPES: ColumnType[] = [
  { type: TableCellType.CIRCLE_BADGE_TITLE, columnKey: "title" },
  { type: TableCellType.TAG, columnKey: "status" },
  { type: TableCellType.TAG, columnKey: "date_due" },
  { type: TableCellType.AVATAR, columnKey: "assigned_to" },
  { type: TableCellType.PRIORITY, columnKey: "priority" },
  { type: TableCellType.AVATAR, columnKey: "last_updated" },
  { type: TableCellType.SELECT, columnKey: "note_count" },
  { type: TableCellType.DELETE, columnKey: "delete" },
];

export const STATUS_MAPPING: { [key in TaskStatus]: BadgeStatus } = {
  [TaskStatus.IN_PROGRESS]: BadgeStatus.IN_PROGRESS,
  [TaskStatus.COMPLETED]: BadgeStatus.COMPLETE,
  [TaskStatus.NOT_STARTED]: BadgeStatus.PLANNED,
  [TaskStatus.STUCK]: BadgeStatus.STUCK,
};

export const PRIORITY_MAPPING: { [key in TaskPriority]: Priority } = {
  [TaskPriority.HIGH]: Priority.HIGH,
  [TaskPriority.MEDIUM]: Priority.MEDIUM,
  [TaskPriority.LOW]: Priority.LOW,
};

// Status is only for colors, does not represent the status of the task
export const TITLE_TAG_COLORS_MAPPING: { [key in "F" | "T"]: BadgeStatus } = {
  F: BadgeStatus.COMPLETE,
  T: BadgeStatus.IN_PROGRESS,
};

export const DELETE_COLUMN: TaskColumnType = {
  title: "Remove",
  key: "delete",
  dataIndex: "delete",
  fixed: "right",
};

export const SUBJECT_TYPE_OPTIONS = [
  { value: SubjectType.NONE, label: "None" },
  { value: SubjectType.FUNDS, label: "Funds" },
  { value: SubjectType.COMPANY, label: "Company" },
];

// Ant Design Select options for priority
export const PRIORITY_OPTIONS = [
  { value: TaskPriority.HIGH, label: "High" },
  { value: TaskPriority.MEDIUM, label: "Medium" },
  { value: TaskPriority.LOW, label: "Low" },
];

// Ant Design Select options for status
export const STATUS_OPTIONS = [
  { value: TaskStatus.NOT_STARTED, label: "Not Started" },
  { value: TaskStatus.IN_PROGRESS, label: "In Progress" },
  { value: TaskStatus.COMPLETED, label: "Completed" },
  { value: TaskStatus.STUCK, label: "Stuck" },
];
